import { Component } from 'react';
import { Modal } from 'react-bootstrap';
import autoBind from 'react-autobind';
import classNames from 'classnames/bind';
import isEqual from 'lodash-es/isEqual';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { Link } from 'react-router-dom';

import moment from 'moment';
import PresetTimePeriod from './PresetTimePeriod';
import SelectedTimePeriod from './SelectedTimePeriod';
import CustomTimePeriod from './CustomTimeFilter/CustomTimePeriod';
import FilterButtonInfo from '../StoryFiltersButton/FilterButtonInfo';

import { periodValues } from '../../../data/filters/storyFilters';
import { showTimeFilterValue, validateEarliestSearchDate } from '../../../helpers/timeFilterHelpers';
import { EARLIEST_DATE_RANGE_ERR, EARLIEST_DAY_ERROR } from '../../../data/filters/filterConstants';
import { GOLD, SILVER } from '../../../data/permissions';
import { CUSTOM_TIME_FILTER } from '../../../data/features';

import withComponentName from '../../../decorators/withComponentName';

import Styles from '../story-filters-styles.module.scss';

const cx = classNames.bind(Styles);

export class TimePeriodFilter extends Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      openFilter: false,
      timeFilter: {
        selected_time_filter: '',
        time_period: '',
        time_filter: '',
        start_datetime: '',
        end_datetime: '',
      },
      displayedName: '',
      buttonErr: false,
    };
  }

  componentDidMount() {
    const {
      savedFilterValue,
      earliestSearchDate,
      features,
      accessLevels,
    } = this.props;

    if (savedFilterValue.selected_time_filter === 'custom' && features.includes(CUSTOM_TIME_FILTER)) {
      const invalidPeriod = validateEarliestSearchDate(
        earliestSearchDate,
        savedFilterValue.start_datetime,
        savedFilterValue.end_datetime,
        accessLevels.includes(GOLD),
      );

      const periodErrors = invalidPeriod === EARLIEST_DATE_RANGE_ERR
        || invalidPeriod === EARLIEST_DAY_ERROR;

      if (periodErrors) this.showButtonError(true);
    }
    this.setState({
      timeFilter: {
        ...savedFilterValue,
      },
      displayedName: showTimeFilterValue(savedFilterValue),
    });
  }

  componentDidUpdate(prevProps) {
    const { type, savedFilterValue } = this.props;

    // only fire this when time changed from another component
    if (!isEqual(prevProps.savedFilterValue, savedFilterValue) && type === 'period') {
      this.setState({
        timeFilter: {
          ...savedFilterValue,
        },
        displayedName: showTimeFilterValue(savedFilterValue),
      });
    }
  }

  setTimeFilter(timeFilter) {
    const { piwikEnabled } = this.props;

    if (piwikEnabled) {
      _paq.push(['trackEvent', 'Filters', `Time period: ${timeFilter}`]);
    }

    this.setState({
      timeFilter: { ...timeFilter },
      buttonErr: false,
    });
  }

  saveTimeFilter() {
    const { timeFilter, buttonErr } = this.state;
    const { action, token } = this.props;

    if (buttonErr) return;

    action(timeFilter, token);
  }

  toggleOpenFilter() {
    const { openFilter, timeFilter, buttonErr } = this.state;

    // do not close modal if user types wrong value
    if (buttonErr && timeFilter.selected_time_filter === 'time_period') return;

    this.setState({ openFilter: !openFilter }, () => {
      const { openFilter: currentValue } = this.state;
      if (!currentValue) this.saveTimeFilter();
    });
  }

  handleApplyFilters() {
    const { timeFilter, buttonErr } = this.state;

    // do not clo0se modal if user types wrong value
    if (buttonErr && timeFilter.selected_time_filter === 'time_period') return;

    this.setState({ openFilter: false });

    this.saveTimeFilter();
  }

  handleCloseModal() {
    const { savedFilterValue } = this.props;

    this.setState({
      timeFilter: {
        ...savedFilterValue,
      },
      displayedName: showTimeFilterValue(savedFilterValue),
      openFilter: false,
      buttonErr: false,
    });
  }

  showButtonError(buttonErr) {
    this.setState({ buttonErr });
  }

  render() {
    const {
      openFilter, timeFilter, displayedName, buttonErr,
    } = this.state;
    const {
      width, disabled, earliestSearchDate, accessLevels, features,
      hasBigScreenDesign, isSentimentLocation,
    } = this.props;

    const goldPlan = accessLevels.includes(GOLD);
    const silverPlan = accessLevels.includes(SILVER);
    const customTimeFilter = features.includes(CUSTOM_TIME_FILTER);

    return (
      <div className={cx('time-period-wrapper', 'story-filters_btn', { disabled: isSentimentLocation })}>
        <button onClick={() => this.toggleOpenFilter()} type="button">
          <FilterButtonInfo
            dropdownTitle="period"
            dropdown={openFilter}
            selectedValue={displayedName}
            buttonErr={buttonErr}
            width={width}
          />
        </button>
        {isSentimentLocation && (
          <div className={cx('tooltip')}>
            This filter is disabled for your
            <br />
            current layout settings
          </div>
        )}
        <Modal
          show={openFilter}
          onHide={() => this.handleCloseModal()}
          centered
          dialogClassName={Styles['time-period-modal-dialog']}
          className={cx('time-period-modal', { big_screen: hasBigScreenDesign, disabledMessage: isSentimentLocation })}
          backdropClassName={Styles['time-period-backdrop']}
        >
          <div
            className={Styles['time-period-modal-header']}
            onClick={this.handleCloseModal}
          >
            <FontAwesomeIcon icon={faXmark} />
          </div>
          {isSentimentLocation ? (
            <div className={cx('modalContainer')}>
              <div className={cx('modalContent')}>
                This filter is disabled for your
                {' '}
                <br />
                current layout settings
              </div>
              <button
                type="button"
                className={cx('modalBtn')}
                onClick={this.handleApplyFilters}
              >
                OK
              </button>
            </div>
          ) : (
            <Modal.Body>
              <h1>TIME PERIOD</h1>
              <div className={Styles['time-filters']}>
                <PresetTimePeriod
                  selectedFilterValue={timeFilter.time_filter}
                  setTimeFilter={this.setTimeFilter}
                  activeTimeFilter={timeFilter.selected_time_filter}
                  periodValues={periodValues.filter((item) => !item.premium)}
                  premiumValues={periodValues.filter((item) => item.premium && item.value !== 'lifetime')}
                  disabled={disabled}
                  accessLevels={accessLevels}
                  gold={goldPlan}
                  silver={silverPlan}
                  customTimeFilter={customTimeFilter}
                  width={width}
                  hasBigScreenDesign={hasBigScreenDesign}
                />
                <div className={cx('selected-custom-filters')}>
                  <SelectedTimePeriod
                    selectedFilterValue={timeFilter.time_period}
                    setTimeFilter={this.setTimeFilter}
                    activeTimeFilter={timeFilter.selected_time_filter}
                    customTimeFilter={customTimeFilter}
                    earliestSearchDate={earliestSearchDate}
                    showButtonError={this.showButtonError}
                    buttonErr={buttonErr}
                    hasBigScreenDesign={hasBigScreenDesign}
                    silver={silverPlan}
                  />
                  <CustomTimePeriod
                    selectedFilterValue={{
                      start_datetime: timeFilter.start_datetime,
                      end_datetime: timeFilter.end_datetime,
                    }}
                    setTimeFilter={this.setTimeFilter}
                    activeTimeFilter={timeFilter.selected_time_filter}
                    width={width}
                    disabled={disabled}
                    customTimeFilter={customTimeFilter}
                    silver={silverPlan}
                    earliestSearchDate={earliestSearchDate}
                    showButtonError={this.showButtonError}
                    buttonErr={buttonErr}
                    goldPlan={goldPlan}
                    hasBigScreenDesign={hasBigScreenDesign}
                    minDate={moment(earliestSearchDate).toDate()}
                  />
                </div>
              </div>
              <div className={Styles['time-period-footer']}>
                <div className={Styles['list-header']}>
                  <FontAwesomeIcon icon={faCircleInfo} className={Styles['exclamation-icon']} />
                  Data available from the last 30 days.
                  {' '}
                  <Link
                    to="/products/api/financial-news/pricing-historical"
                  >
                    Historical data
                  </Link>
                  {' '}
                  can be purchased separately in CSV format.
                </div>
              </div>
              <div className={cx('btn-group')}>
                <button type="button" className={cx('cancel-btn')} onClick={() => this.handleCloseModal()}>Cancel</button>
                <button type="button" className={cx('save-btn', { disabled: buttonErr })} onClick={() => this.handleApplyFilters()}>Save</button>
              </div>
            </Modal.Body>
          )}
        </Modal>
      </div>
    );
  }
}

export default withComponentName(TimePeriodFilter);
